import { Event as Events } from '@planjs/utils';
import {
  CloseMsg,
  ConnectionAdapter,
  SocketClient,
  SocketOptions,
  SocketDataType,
} from './interfaces';
import { isPropertyExist, isRealObject } from './utils';
import { ReadyState } from './enums';
import { Response } from './response';

const noop = function () {};
const w = typeof window === 'object' ? window : noop;
export const ArrayBuffer: ArrayBufferConstructor = (() => (w as any).ArrayBuffer || noop)();

export class Socket {
  private readonly options: SocketOptions;
  private client: ConnectionAdapter;
  private task!: SocketClient | null;
  private events = new Events();

  private heartbeat: {
    ping: boolean;
    duration: number;
    data: ArrayBuffer | string;
    timerID: number;
  } = {
    ping: false,
    duration: 0,
    data: 'ping',
    timerID: 0,
  };

  constructor(client: ConnectionAdapter, options: SocketOptions) {
    if (!isPropertyExist<SocketOptions>(options, 'autoOpen')) {
      options.autoOpen = true;
    }
    this.options = options;
    this.client = client;

    // auto open socket
    options.autoOpen && this.open();
  }

  private _checkTask() {
    if (!this.task) {
      return Promise.reject(new Error('Please open socket'));
    }
  }

  private _clearPingTimer() {
    if (this.heartbeat.timerID) clearTimeout(this.heartbeat.timerID);
    this.heartbeat.timerID = 0;
  }

  /**
   * 发送ping
   * @private
   */
  private _doPing = () => {
    if (this.heartbeat.ping) {
      this._clearPingTimer();
      this.heartbeat.timerID = setTimeout(() => {
        const res = this.send(this.heartbeat.data);
        this.events.trigger('onPing', res);
        res.catch(err => {
          console.error('ws ping error', err);
        });
      }, this.heartbeat.duration) as unknown as number;
    }
  };

  // TODO 检查发包格式
  private _processRequestData = (data: SocketDataType): string | ArrayBuffer | null => {
    if (typeof data === 'string') {
      return data;
    } else if (data instanceof ArrayBuffer) {
      return data;
    } else if (isRealObject(data)) {
      return JSON.stringify(data);
    } else {
      return null;
    }
  };

  removeListener() {
    this.events.off();
  }

  open(): SocketClient {
    if (this.task) return this.task;
    return (this.task = this.client.createConnection(this.options, { events: this.events }));
  }

  /**
   * close之前的ws实例，重新初始化一个ws实例
   * close msg { code: 1000, reason: 'reopen' }
   * @desc 保持之前注册的事件，重新打开一个链接
   *       如果需要移除之前的事件调用 removeListener
   */
  reOpen(): SocketClient {
    try {
      this.close({ code: 1000, reason: 'reopen' });
      this.task = null;
    } catch (e) {
      console.log(e);
    }
    this.open();
    if (this.heartbeat.ping) {
      this.ping(this.heartbeat.duration, this.heartbeat.data);
    }
    return this.task!;
  }

  /**
   * socket 还没死的情况下隔短时间会发送一个ping的包，如果有消息发送也相当于一个ping（发消息同时不会发送ping）
   * 可以 onOpen的时候触发一下
   * @param duration
   * @param data
   */
  ping(duration: number, data?: string | ArrayBuffer | object) {
    this.heartbeat.duration = duration;
    this.heartbeat.ping = true;
    const ret = this._processRequestData(data!);
    if (ret) {
      this.heartbeat.data = ret;
    }
    this._doPing();
    this.events.off('onMessage', this._doPing);
    this.onMessage(this._doPing);
  }

  cancelPing() {
    this.heartbeat.ping = false;
    this._clearPingTimer();
  }

  /**
   * ws 发送消息
   * @param data
   */
  async send(data: string | ArrayBuffer | object): Promise<Response> {
    await this._checkTask();
    if (this.task?.readyState !== ReadyState.OPEN) {
      return Promise.reject(
        new Error(`Socket client is ${ReadyState[this.task!.readyState]}.\nCan't send message`)
      );
    }
    const ret = this._processRequestData(data);
    this._doPing();
    if (ret) {
      return this.task?.send(ret);
    }
    return Promise.reject(new Error('Data is not supported.'));
  }

  /**
   * websocket close
   * @param msg
   * @doc https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent
   */
  close(msg?: CloseMsg) {
    if (this.task?.readyState! in [ReadyState.CONNECTING, ReadyState.OPEN]) {
      this.task!.close(msg);
    }
    this._clearPingTimer();
    this.task = null;
  }

  /**
   * ws打开时会触发
   * @param fn
   */
  onOpen(fn: (task: SocketClient) => void) {
    this.events.on('onOpen', fn);
  }

  onMessage(fn: (res: Response) => void) {
    this.events.on('onMessage', fn);
  }

  onError(fn: (res: Response) => void) {
    this.events.on('onError', fn);
  }

  /**
   * 关闭事件
   * @desc Response.OK 代表是正常退出，code === 1000
   * @param fn
   */
  onClose(fn: (res: Response) => void) {
    this.events.on('onClose', fn);
  }

  /**
   * state change event
   */
  onReadyStateChange(fn: (task: SocketClient) => void) {
    this.events.on('onReadyStateChange', fn);
  }

  /**
   * ping 的结果
   * @param fn
   */
  onPing(fn: (res: Promise<Response>) => void) {
    this.events.on('onPing', fn);
  }

  get readyState() {
    return this.task?.readyState;
  }
}
